import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import "aos/dist/aos.css";
import AOS from "aos";
import { Link } from "react-router-dom";
import {
  FaFolder,
  FaStar,
  FaBuilding,
  FaShieldAlt,
  FaHandshake,
  FaLightbulb,
  FaAward,
  FaTasks,
  FaUsers,
  FaBullseye,
  FaRocket,
  FaEye,
  FaCogs,
  FaServer,
  FaCode,
  FaBullhorn,
} from "react-icons/fa";

const AboutUs = ({ name }) => {
  const [counterValues, setCounterValues] = useState({
    experts: 0,
    certifications: 0,
    offices: 0,
  });
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(true);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const whyItems = [
    {
      side: "right",
      title: "Skilled & Passionate Professionals",
      desc: "Our team combines strong technical knowledge with creativity and a problem-solving mindset, helping businesses stay ahead in a constantly evolving digital world.",
    },
    {
      side: "left",
      title: "Strong Industry Knowledge",
      desc: "With experience across multiple industries, we understand real business challenges and deliver practical, effective and high-impact solutions.",
    },
    {
      side: "right",
      title: "Reliable Support & Service",
      desc: "We’re approachable, dependable, and always ready to assist, ensuring your operations run smoothly without interruption.",
    },
    {
      side: "left",
      title: "Results That Matter",
      desc: "We aim for meaningful outcomes, not just completed tasks, ensuring every solution creates measurable value for your business.",
    },
    {
      side: "right",
      title: "Client-Centric Approach",
      desc: "We take the time to understand your goals and tailor our services to match them, so every solution fits your needs perfectly.",
    },
    {
      side: "left",
      title: "All-in-One Digital Partner",
      desc: "From managing IT infrastructure and building custom software to handling branding and digital marketing, we provide end-to-end support for your complete digital journey.",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: "ease-out" });

    // Counters Animation
    const target = { experts: 130, certifications: 45, offices: 2 };
    const duration = 1200;
    const steps = 60;
    const inc = {
      experts: target.experts / steps,
      certifications: target.certifications / steps,
      offices: target.offices / steps,
    };
    let s = 0;
    const t = setInterval(() => {
      if (s >= steps) {
        clearInterval(t);
        setCounterValues(target);
        return;
      }
      setCounterValues((p) => ({
        experts: Math.min(Math.round(p.experts + inc.experts), target.experts),
        certifications: Math.min(
          Math.round(p.certifications + inc.certifications),
          target.certifications
        ),
        offices: Math.min(Math.round(p.offices + inc.offices), target.offices),
      }));
      s++;
    }, duration / steps);

    return () => clearInterval(t);
  }, []);

  // Scroll Progress & Back-to-Top Visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / scrollHeight) * 307.919;
      setScrollProgress(progress);

      setShowScroll(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check carousel scroll position for arrow visibility
  useEffect(() => {
    const checkScrollPosition = () => {
      const scrollContainer = document.getElementById("valuesScroll");
      if (scrollContainer) {
        const currentScroll = scrollContainer.scrollLeft;
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        
        setCanScrollLeft(currentScroll > 0);
        setCanScrollRight(currentScroll < maxScroll - 1);
      }
    };

    checkScrollPosition();
    
    const scrollContainer = document.getElementById("valuesScroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollPosition);
      return () => scrollContainer.removeEventListener("scroll", checkScrollPosition);
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>
          About SecurXpert Technologies | IT Solutions & Digital Services
        </title>
        <meta
          name="description"
          content="Learn about SecurXpert Technologies - trusted IT solutions, software development, and digital marketing services. Discover our mission, values, and commitment to secure digital transformation."
        />
        <meta
          name="keywords"
          content="SecurXpert Technologies, IT solutions, software development, digital marketing, cybersecurity, cloud services, digital transformation"
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <header className="relative w-full">
        <div
          className="relative w-full h-[317px] bg-top bg-cover bg-no-repeat "
          style={{ backgroundImage: "url(/img/whoweare.png)" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-md">
              Who We Are
            </h1>

            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Who We Are</span>
            </div>
          </div>
        </div>
      </header>

      {/* WELCOME SECTION */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-8 sm:py-12 md:py-16 lg:py-19 bg-white">
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 md:gap-10 lg:gap-11 justify-center items-center w-full">
          <div className="flex flex-col gap-4 sm:gap-5 md:gap-5.5 lg:gap-5.5 justify-start items-start w-full lg:w-1/2">
            <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[40px] font-bold leading-tight text-[#210a4a]">
              Welcome to
              <br />
              SecurXpert Technologies
            </h2>
            <p className="text-sm sm:text-base font-normal leading-6 text-[#5f5d5d] w-full">
              SecurXpert Technologies is a future-focused digital and technology
              solutions company delivering innovative, scalable and secure
              solutions for modern businesses. We specialize in AI-driven
              platforms, cloud infrastructure, UI/UX design and enterprise
              software development to help organizations grow digitally with
              confidence.
            </p>
          </div>

          <div className="flex justify-center items-center w-full lg:w-auto">
            <div className="border border-[#210a4a] rounded-r-[50px] sm:rounded-r-[75px] md:rounded-r-[100px] p-2 sm:p-3 md:p-3.5">
              <img
                src="/img/whoheroi.jpg"
                className="w-full max-w-[270px] sm:max-w-[400px] md:max-w-[480px] lg:w-[538px] h-auto rounded-r-[50px] sm:rounded-r-[75px] md:rounded-r-[100px] object-cover"
                alt="SecurXpert Technologies team"
              />
            </div>
          </div>
        </div>

        {/* Purpose, Mission, Vision, What We Do */}
        <div className="flex flex-col lg:flex-row justify-center items-center w-full gap-6 sm:gap-8 md:gap-10 lg:gap-11 mt-12 sm:mt-16 md:mt-18 lg:mt-20.5">
          <div className="flex justify-center items-center w-full lg:w-1/2">
            <div className="flex justify-center items-center w-full relative max-w-[500px] mx-auto">
              <div className="flex justify-center items-center w-full relative z-0 lg:ml-5">
                <img
                  src="/img/purpose.png"
                  className="w-[280px] sm:w-[260px] md:w-[320px] lg:w-[460px]
                             h-[300px] sm:h-[320px] md:h-[380px] lg:h-[500px]
                             rounded-t-[18px] sm:rounded-t-[24px] md:rounded-t-[30px] lg:rounded-t-[36px]
                             object-cover mb-4 sm:mb-5 md:mb-6 lg:mb-6.5 z-20 mx-auto"
                  alt="Team working"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6 sm:gap-8 md:gap-9 lg:gap-10.5 w-full lg:w-1/2">
            {[
              {
                icon: FaEye,
                title: "Our Vision",
                desc: "To become a trusted leader in secure digital transformation, recognized for our innovation, dependability and client-first approach.",
                pr: true,
              },
              {
                icon: FaRocket,
                title: "Our Mission",
                desc: "To create meaningful change through IT innovation and strategic digital solutions, helping businesses grow sustainably, stay flexible and achieve measurable outcomes.",
                pr: false,
              },
              {
                icon: FaBullseye,
                title: "Our Purpose",
                desc: "We enable businesses to succeed through intelligent technologies, cloud solutions and innovative digital strategies. Our focus is on making every digital experience smooth, effective and impactful.",
                pr: true,
              },
              {
                icon: FaCogs,
                title: "What We Do",
                desc: "We provide end-to-end technology and digital services designed to empower businesses to grow, optimize performance and stay ahead in today’s competitive digital landscape.",
                pr: true,
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`flex justify-start items-start w-full ${
                    item.pr ? "pr-0 sm:pr-2 md:pr-2.5" : ""
                  }`}
                >
                  <div className="flex gap-3 sm:gap-4 md:gap-4.5 lg:gap-5 justify-start items-start w-full">
                    <div className="flex justify-center items-center w-[50px] sm:w-[60px] md:w-[70px] lg:w-[80px] h-[50px] sm:h-[60px] md:h-[70px] lg:h-[80px] border border-[#3e66f3] rounded-[20px] sm:rounded-[25px] md:rounded-[30px] lg:rounded-[40px] p-3 sm:p-4 md:p-4.5 lg:p-4.5 flex-shrink-0">
                      <Icon className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] text-[#3e66f3]" />
                    </div>
                    <div className="flex flex-col justify-start items-start w-full">
                      <h3 className="text-[18px] sm:text-[20px] md:text-[24px] lg:text-[28px] font-bold leading-tight text-[#210a4a]">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full mt-1">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Services Cards */}
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 lg:gap-6 w-full mt-8 sm:mt-10 md:mt-12 lg:mt-16 justify-center items-stretch">
          {[
            {
              icon: FaServer,
              title: "IT Solutions",
              desc: "We deliver reliable infrastructure setup, cloud support, data security, and managed IT services to keep your business operations smooth, protected and continuously efficient.",
            },
            {
              icon: FaCode,
              title: "Development",
              desc: "Our team builds custom web and mobile applications tailored to real business challenges, ensuring intuitive interfaces, strong performance and seamless user experiences.",
            },
            {
              icon: FaBullhorn,
              title: "Digital Marketing",
              desc: "We provide SEO, social media management, performance campaigns, and brand-building strategies that boost visibility, attract ideal audiences and grow your digital presence.",
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="flex justify-start items-center w-full md:w-[350px] lg:w-[380px]"
              >
                <div className="flex flex-col justify-start items-start w-full">
                  <div className="relative z-20 flex justify-start items-center w-auto bg-[#210a4a] rounded-[19px] sm:rounded-[28px] md:rounded-[32px] lg:rounded-[38px] p-3 sm:p-3.5 md:p-4 lg:p-4.5 ml-2 sm:ml-2.5 md:ml-3 lg:ml-3">
                    <Icon className="text-[24px] sm:text-[30px] md:text-[36px] lg:text-[42px] text-white" />
                  </div>

                  <div className="relative z-10 flex flex-col justify-start items-start w-full bg-white border-t-[3px] border-l-[3px] border-[#210a4a] rounded-[10px] shadow-[3px_5px_5px_rgba(0,0,0,0.1)] p-6 sm:p-7 md:p-8 lg:p-9 -mt-4 sm:-mt-5 md:-mt-6 lg:-mt-7">
                    <h3 className="text-[18px] sm:text-[22px] md:text-[26px] lg:text-[30px] font-semibold leading-tight text-black mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ────────────────────────────────────────────────
          FIXED IMPACTFUL NUMBERS SECTION
      ──────────────────────────────────────────────── */}
      <section className="relative py-12 sm:py-14 lg:py-20 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent"
          style={{ backgroundColor: "#080235ff" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="text-center mb-8" data-aos="fade-up">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
              Impactful Numbers Reflecting Our Commitments
            </h2>
            <p className="text-purple-100 mt-3 max-w-3xl mx-auto text-sm sm:text-base">
              These numbers showcase our expertise, trust from clients, and
              consistent growth as a leading IT partner:
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-stretch gap-6 sm:gap-8 lg:gap-12 mb-8">
            {[
              {
                icon: <FaFolder className="text-purple-200 text-2xl sm:text-3xl" />,
                value: `${counterValues.experts}+`,
                subtitle: "Tech ECoreXperts & Security Specialists",
              },
              {
                icon: <FaStar className="text-purple-200 text-2xl sm:text-3xl" />,
                value: `${counterValues.certifications}+`,
                subtitle: "Industry Certifications & Recognitions",
              },
              {
                icon: <FaBuilding className="text-purple-200 text-2xl sm:text-3xl" />,
                value: counterValues.offices,
                subtitle: "Our Offices",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="
                  flex flex-col items-center text-center 
                  bg-[#3E66F3]/20 backdrop-blur-sm 
                  rounded-2xl p-4 sm:p-5 
                  border border-white/20 
                  min-w-[180px] sm:min-w-[200px] lg:min-w-[220px]
                  w-full
                  flex-1
                "
                data-aos="zoom-in"
                data-aos-delay={index * 200}
              >
                <div className="mb-3 p-3 bg-white/20 rounded-full">
                  {item.icon}
                </div>
                <div className="
                  text-2xl sm:text-3xl lg:text-4xl 
                  font-extrabold text-white mb-2 drop-shadow-lg
                  min-h-[1em] flex items-center justify-center
                ">
                  {item.value}
                </div>
                <h6 className="
                  text-purple-100 font-semibold 
                  text-xs sm:text-sm 
                  min-h-[3rem] sm:min-h-[3.5rem] 
                  flex items-center justify-center 
                  text-center px-2 sm:px-3
                ">
                  {item.subtitle}
                </h6>
              </div>
            ))}
          </div>

          <div className="text-center" data-aos="fade-up">
            <p className="text-purple-100 max-w-3xl mx-auto text-sm sm:text-base">
              From Hyderabad to global clients, these milestones reflect our
              dedication to secure, innovative, and client-focused digital
              transformation.
            </p>
          </div>
        </div>
      </section>

      {/* CORE VALUES – horizontal scroll */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-12 sm:py-14 md:py-16 lg:py-18 bg-white">
        <div className="flex flex-col justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Core Values Driving Our Excellence
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-lg font-normal leading-tight text-[#5f5d5d] text-center mt-2 sm:mt-3 md:mt-4 lg:mt-4 max-w-4xl">
            Our core values define who we are and how we deliver value. We
            believe in innovation, transparency, customer-centricity and
            continuous improvement to deliver impactful digital solutions.
          </p>

          <div className="relative w-full overflow-visible">
            <button
              onClick={() => {
                const scrollContainer = document.getElementById("valuesScroll");
                if (scrollContainer) {
                  const currentScroll = scrollContainer.scrollLeft;
                  const newScroll = Math.max(0, currentScroll - 250);
                  scrollContainer.scrollTo({ left: newScroll, behavior: "smooth" });
                }
              }}
              disabled={!canScrollLeft}
              className={`
                flex absolute -left-1 sm:left-2 top-1/2 -translate-y-1/2 
                bg-white shadow-xl w-11 h-11 sm:w-12 sm:h-12 rounded-full 
                items-center justify-center text-[#3e66f3] hover:bg-[#3e66f3]
                transition-all duration-200 z-[5] border-2 border-gray-300 md:border-gray-200
                ${!canScrollLeft ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:scale-105'}
              `}
            >
              <i className="fa-solid fa-chevron-left text-base sm:text-xl"></i>
            </button>

            <div
              id="valuesScroll"
              className="w-full mt-8 sm:mt-10 md:mt-12 lg:mt-14 pb-4 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex flex-nowrap justify-start gap-4 sm:gap-5 md:gap-6 lg:gap-7 min-w-max">
                {[
                  { icon: FaShieldAlt, title: "Security", desc: "We design every solution with strong protection and privacy in mind, ensuring your data and systems stay safe at all times." },
                  { icon: FaHandshake, title: "Integrity", desc: "We follow honest, transparent, and ethical practices in everything we do, building trust through our actions and results." },
                  { icon: FaLightbulb, title: "Innovation", desc: "We use AI, automation, and cutting-edge technologies to create smarter, forward-thinking solutions that move businesses ahead." },
                  { icon: FaAward, title: "Excellence", desc: "We focus on high quality, continuous improvement, and delivering results that consistently exceed expectations." },
                  { icon: FaTasks, title: "Responsibility", desc: "We take ownership of our work, stand by our commitments, and ensure every solution reflects accountability and professionalism." },
                  { icon: FaUsers, title: "Collaboration", desc: "We believe in growing together by working closely with our clients, partners, and team to achieve shared success." },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex-none w-[280px] sm:w-[300px] md:w-[320px] lg:w-[254px] bg-white border-b-[8px] hover:border-[#3e66f3] rounded-[25px] sm:rounded-[35px] md:rounded-[42px] lg:rounded-[50px] shadow-[0px_1px_5px_rgba(0,0,0,0.1)] p-5 sm:p-6 md:p-7 lg:p-6.5"
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                    >
                      <div className="mx-auto mb-3 flex items-center justify-center">
                        <Icon className="text-[32px] sm:text-[40px] md:text-[47px] lg:text-[54px] text-[#3e66f3]" />
                      </div>
                      <h3 className="text-[16px] sm:text-[20px] md:text-[24px] lg:text-[28px] font-bold leading-tight text-[#210a4a] text-center w-full mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm sm:text-base md:text-lg lg:text-lg font-normal leading-tight text-[#5f5d5d] text-center w-full">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              onClick={() => {
                const scrollContainer = document.getElementById("valuesScroll");
                if (scrollContainer) {
                  const currentScroll = scrollContainer.scrollLeft;
                  const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
                  const newScroll = Math.min(maxScroll, currentScroll + 250);
                  scrollContainer.scrollTo({ left: newScroll, behavior: "smooth" });
                }
              }}
              disabled={!canScrollRight}
              className={`
                flex absolute -right-1 sm:right-2 top-1/2 -translate-y-1/2 
                bg-white shadow-xl w-11 h-11 sm:w-12 sm:h-12 rounded-full 
                items-center justify-center text-[#3e66f3] hover:bg-[#3e66f3]  
                transition-all duration-200 z-[5] border-2 border-gray-300 md:border-gray-200
                ${!canScrollRight ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:scale-105'}
              `}
            >
              <i className="fa-solid fa-chevron-right text-base sm:text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* WHY SECURXPERT */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-7 sm:py-10 md:py-12 lg:py-14 bg-white">
        <div className="flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-8 justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Why SecurXpert Technologies?
          </h2>
          <p>
            We combine deep technical expertise with industry best practices to
            deliver reliable, scalable, and secure digital solutions. Our team
            focuses on quality, innovation and long-term partnerships.
          </p>

          <div className="relative w-full max-w-5xl mx-auto">
            <div
              className="hidden sm:block absolute left-1/2 -translate-x-1/2 bg-[#210a4a] rounded-full"
              style={{ width: "3px", top: "27px", bottom: "30px" }}
            />

            {whyItems.map((item, index) => {
              const isLeft = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="mb-12 sm:mb-16 relative"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="hidden sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                    <div className={`pr-6 ${isLeft ? "text-right" : ""}`}>
                      {isLeft && (
                        <>
                          <h3 className="text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] font-semibold text-[#210a4a] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-[#5f5d5d] leading-6">
                            {item.desc}
                          </p>
                        </>
                      )}
                    </div>

                    <div className="flex items-center justify-center relative z-10">
                      <div className="w-[54px] h-[54px] rounded-full bg-[#210a4a] text-white flex items-center justify-center text-xl font-semibold shadow-md">
                        {index + 1}
                      </div>
                    </div>

                    <div className={`pl-6 ${!isLeft ? "text-left" : ""}`}>
                      {!isLeft && (
                        <>
                          <h3 className="text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] font-semibold text-[#210a4a] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-[#5f5d5d] leading-6">
                            {item.desc}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="sm:hidden flex flex-col items-center text-center gap-3">
                    <div className="w-[42px] h-[42px] rounded-full bg-[#210a4a] text-white flex items-center justify-center text-base font-semibold mb-1">
                      {index + 1}
                    </div>
                    <h3 className="text-[16px] font-semibold text-[#210a4a]">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#5f5d5d] leading-5 max-w-xs">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY TO DIGITAL EXCELLENCE */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-8 sm:py-10 md:py-12 lg:py-14 bg-white">
        <div className="flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-8.5 justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Join Us on the Journey to
            <br />
            Digital Excellence
          </h2>
          <p>
            Join us as we drive digital transformation through cutting-edge
            technologies, innovative thinking and a commitment to excellence.
          </p>

          <div className="flex flex-col md:flex-row gap-8 sm:gap-10 md:gap-12 lg:gap-16 w-full max-w-6xl justify-center items-stretch">
            {[
              {
                icon: FaShieldAlt,
                title: "Secure Solutions",
                desc: "We protect your business with strong, reliable security practices that safeguard your data and build lasting trust.",
              },
              {
                icon: FaLightbulb,
                title: "Innovative IT Solutions",
                desc: "We use advanced technologies to create smart, forward-thinking solutions designed specifically to fit your business needs.",
              },
              {
                icon: FaHandshake,
                title: "Trusted Partnership",
                desc: "We believe in long-term relationships built on honesty, consistency, and exceptional support you can rely on every day.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex flex-col justify-start items-center w-full md:w-[320px] lg:w-[346px] bg-white border-b-[4px] border-[#3e66f3] rounded-b-[5px] shadow-[0px_-2px_4px_rgba(0,0,0,0.1)] p-6 sm:p-7 md:p-8 lg:p-8 text-center"
                  data-aos="zoom-in"
                >
                  <Icon className="text-[32px] sm:text-[40px] md:text-[47px] lg:text-[54px] text-[#3e66f3] mb-4" />
                  <h3 className="text-[16px] sm:text-[18px] md:text-[21px] lg:text-[25px] font-bold leading-tight text-[#210a4a] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      {/* SCROLL TO TOP */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: "stroke-dashoffset 0.1s linear" }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AboutUs;